/**
 * Iframe-side post-condition evaluator. Runs after each action settles to
 * report whether the action achieved the goal the agent declared via
 * `expects`. Pure function over a fresh DOM read — safe to call multiple
 * times per action.
 *
 * Mirror of `evaluatePostConditionFromUiState` in
 * `packages/server/src/agents/tools/post-condition.ts` but with full DOM
 * access, so it can decide the cases the server-side slim view can't
 * (aria_expanded, aria_pressed, value_set, element_visible).
 */

import type { ModalSnapshot } from '../capture/element-mapper.js';
import { captureOpenModals } from '../capture/element-mapper.js';

/**
 * Structural copy of {@link PostCondition} from the server. Duplicated so
 * the agent bundle stays free of server-package imports — the bundle is
 * shipped to visitors and we keep its surface area tight.
 */
export type PostCondition =
  | { kind: 'modal_open'; id?: string; titleContains?: string }
  | { kind: 'modal_closed' }
  | { kind: 'aria_expanded'; value: boolean }
  | { kind: 'aria_pressed'; value: boolean }
  | { kind: 'value_set'; value: string }
  | { kind: 'route_matches'; pattern: string }
  | { kind: 'element_visible'; selector?: string }
  | { kind: 'text_appears'; text: string }
  | { kind: 'text_disappears'; text: string };

export type PostConditionState = 'satisfied' | 'unsatisfied' | 'unknown';

/**
 * Evaluate a post-condition against the current DOM. Optional `subject`
 * is the element the action targeted (for aria_*, value_set,
 * element_visible). When omitted and the condition needs a subject,
 * returns 'unknown'.
 */
export function evaluatePostCondition(
  cond: PostCondition,
  subject?: Element | null,
): PostConditionState {
  switch (cond.kind) {
    case 'modal_open': {
      const open = captureOpenModals();
      if (open.length === 0) return 'unsatisfied';
      if (cond.id) {
        return matchesModalId(open, cond.id) ? 'satisfied' : 'unsatisfied';
      }
      if (cond.titleContains) {
        const needle = cond.titleContains.toLowerCase();
        return open.some((m) => (m.title ?? '').toLowerCase().includes(needle))
          ? 'satisfied'
          : 'unsatisfied';
      }
      return 'satisfied';
    }
    case 'modal_closed':
      return captureOpenModals().length === 0 ? 'satisfied' : 'unsatisfied';
    case 'aria_expanded': {
      if (!subject) return 'unknown';
      const attr = subject.getAttribute('aria-expanded');
      if (attr === null) return 'unknown';
      return (attr === 'true') === cond.value ? 'satisfied' : 'unsatisfied';
    }
    case 'aria_pressed': {
      if (!subject) return 'unknown';
      const attr = subject.getAttribute('aria-pressed');
      if (attr === null) return 'unknown';
      return (attr === 'true') === cond.value ? 'satisfied' : 'unsatisfied';
    }
    case 'value_set': {
      if (!subject) return 'unknown';
      const v = readValue(subject);
      if (v === undefined) return 'unknown';
      return v === cond.value ? 'satisfied' : 'unsatisfied';
    }
    case 'route_matches': {
      try {
        const re = new RegExp(cond.pattern);
        return re.test(location.pathname + location.search) ? 'satisfied' : 'unsatisfied';
      } catch {
        return 'unknown';
      }
    }
    case 'element_visible': {
      const el = cond.selector
        ? document.querySelector(cond.selector)
        : subject;
      if (!el) return 'unsatisfied';
      const rect = (el as HTMLElement).getBoundingClientRect?.();
      if (!rect) return 'unknown';
      const style = (el as HTMLElement).ownerDocument?.defaultView?.getComputedStyle?.(el as Element);
      if (style && (style.display === 'none' || style.visibility === 'hidden')) return 'unsatisfied';
      return rect.width > 0 && rect.height > 0 ? 'satisfied' : 'unsatisfied';
    }
    case 'text_appears':
      return scanVisibleTextContains(cond.text) ? 'satisfied' : 'unsatisfied';
    case 'text_disappears':
      return scanVisibleTextContains(cond.text) ? 'unsatisfied' : 'satisfied';
  }
}

/**
 * Case-insensitive substring scan against visible text in the document.
 * Reads `document.body.innerText` rather than `textContent` so that hidden
 * subtrees (display:none, visibility:hidden) are excluded by the browser's
 * own visibility rules — we don't have to re-walk and filter ourselves.
 *
 * Trim the haystack to a sane upper bound (256KB) before searching so a
 * pathologically large page doesn't stall the verifier.
 */
function scanVisibleTextContains(needle: string): boolean {
  const trimmed = needle.trim().toLowerCase();
  if (!trimmed) return false;
  let haystack = '';
  try {
    haystack = (document.body?.innerText ?? '').toLowerCase();
  } catch {
    return false;
  }
  if (haystack.length > 256_000) haystack = haystack.slice(0, 256_000);
  return haystack.includes(trimmed);
}

function matchesModalId(open: ModalSnapshot[], id: string): boolean {
  // Modals don't carry a stable id in the snapshot today — match by
  // trigger selector, title, or a substring tail of either. PDP authors
  // typically id a modal as `<element_id>_modal` so check both halves.
  const lower = id.toLowerCase();
  const stripped = lower.replace(/_modal$/, '').replace(/_dialog$/, '');
  for (const m of open) {
    const trigger = (m.trigger ?? '').toLowerCase();
    const title = (m.title ?? '').toLowerCase();
    if (trigger.includes(lower) || title.includes(lower)) return true;
    if (stripped && (trigger.includes(stripped) || title.includes(stripped))) return true;
  }
  return false;
}

function readValue(el: Element): string | undefined {
  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) {
    return el.value;
  }
  return undefined;
}
